"use client"

import React, { useRef, useEffect, useState, useCallback, useMemo } from "react"
import { ThemeProvider } from '@mui/material/styles'
import { Box, TextField, Button, SwipeableDrawer } from '@mui/material'
import MessageList from "@/components/chat/MessageList"
import { Send } from '@mui/icons-material'
import theme from '@/theme'
import { useTranslation } from 'react-i18next'
import { useWelcomeSpreadContext } from '@/context/WelcomeSpreadContext'
import CardSelection from '@/components/reading/CardSelection'
import { TarotCard } from '@/context/CardsContext'
import { track } from '@/third-party/MixPanel'
import useCards from '@/hooks/useCards'
import type { Message } from '@/pages/chat/types/chat'

function WelcomeSpread() {
  const { t } = useTranslation()
  const {
    messages,
    isTyping,
    isLoading,
    error,
    sendMessage: sendMessageToContext,
    addMessage
  } = useWelcomeSpreadContext()

  const { getTextTarotReading, setDrawnCards } = useCards()

  const [input, setInput] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [selectionDrawerOpen, setSelectionDrawerOpen] = useState(false)
  const [selectionKey, setSelectionKey] = useState(0)
  const [cardsChosen, setCardsChoosen] = useState(false)
  const [selectedCards, setSelectedCards] = useState<TarotCard[]>([])
  const [isGeneratingReading, setIsGeneratingReading] = useState(false)

  // Auto-scroll to bottom when messages change or typing status changes
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }
    scrollToBottom()
  }, [messages, isTyping])

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    const messageContent = input
    setInput("") // Clear input immediately for better UX

    // Send message through context
    await sendMessageToContext(messageContent)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
  }

  const openSelectionDrawer = useCallback(() => {
    track('welcome_spread_card_selection_opened')
    setSelectionDrawerOpen(true)
  }, [])

  const closeSelectionDrawer = useCallback(() => {
    track('welcome_spread_card_selection_closed', {
      cardsSelected: selectedCards.length
    })
    setSelectionDrawerOpen(false)
    // Increment key to force remount and reset selection
    setSelectionKey(prev => prev + 1)
  }, [selectedCards])

  // Find the step 1 message to get the spread ID
  const step1Message = messages.find(msg => msg.step === 1 && msg.spreadId)

  // Mapping of spread types to number of cards
  const spreadCardCounts: { [key: string]: number } = {
    'one-card-reading': 1,
    'three-card': 3,
    'celtic-cross': 10,
    'horseshoe-spread': 7,
    'tetraktys-spread': 10,
    'tree-of-life': 10,
    'astrological-spread': 12,
    'year-ahead-spread': 12,
  }

  // Generate or use existing cards for the spread
  useEffect(() => {
    if (step1Message && step1Message.spreadId && selectedCards.length === 0) {
      const cardCount = spreadCardCounts[step1Message.spreadId] || 3

      // Generate fake cards array based on spread type
      const fakeCards = Array.from({ length: cardCount }, (_, index) => ({
        id: Math.floor(Math.random() * 78), // Random card ID (0-77 for tarot deck)
        visible: true,
        showFront: false, // Show the back of the card
        reversed: Math.random() > 0.5, // Random reversed state
      }))

      setSelectedCards(fakeCards as TarotCard[])
      console.log(step1Message.spreadId, "spread type", fakeCards.length, "cards", fakeCards)
    }
  }, [step1Message, selectedCards.length, spreadCardCounts])

  // Create spread message when step1Message first appears
  useEffect(() => {
    if (step1Message && step1Message.spreadId && !messages.some(msg => msg.type === 'spread')) {
      const spreadMessage: Message = {
        id: `spread-${Date.now()}`,
        content: '', // Empty content for spread messages
        role: "assistant" as const,
        timestamp: new Date(),
        type: "spread",
        spreadData: {
          spreadType: step1Message.spreadId,
          cards: selectedCards,
          canChooseCards: !cardsChosen,
          onChooseCards: openSelectionDrawer
        }
      }
      addMessage(spreadMessage)
    }
  }, [step1Message, messages, selectedCards, cardsChosen, openSelectionDrawer, addMessage])

  // Enrich messages with updated spread data
  const enrichedMessages = useMemo(() => {
    return messages.map(msg => {
      if (msg.type === 'spread' && step1Message) {
        // Return updated spread message with current state
        return {
          ...msg,
          spreadData: {
            spreadType: step1Message.spreadId || 'three-card',
            cards: selectedCards,
            canChooseCards: !cardsChosen,
            onChooseCards: openSelectionDrawer
          }
        }
      }
      return msg
    })
  }, [messages, selectedCards, cardsChosen, step1Message, openSelectionDrawer])

  const handleSelectionComplete = useCallback(async (cards: TarotCard[]) => {
    console.log('Selected cards:', cards)

    if (cards.length > 0) {
      track('welcome_spread_cards_confirmed', {
        cardCount: cards.length,
        cardIds: cards.map(c => c.id)
      })
      setSelectedCards(cards)
      setCardsChoosen(true)

      // Add system message thanking for choosing cards
      const systemMessage: Message = {
        id: `system-${Date.now()}`,
        content: t('welcomeSpread.thanksForChoosingCards') || 'Thanks for choosing your cards, now I will read them',
        role: "assistant" as const,
        timestamp: new Date(),
      }
      addMessage(systemMessage)

      // Close drawer after receiving selection
      closeSelectionDrawer()

      // Get the step1 payload from localStorage to retrieve the user question
      const step1Payload = localStorage.getItem('welcomeSpread_step1')
      let userQuestion = ''

      if (step1Payload) {
        const payload = JSON.parse(step1Payload)
        userQuestion = payload.userQuestion || ''
      }

      // Store cards in context and localStorage for getTextTarotReading
      setDrawnCards(cards)

      // Store reading metadata in localStorage for getTextTarotReading
      const readingMetadata = {
        question: userQuestion,
        selectedReading: {
          type: step1Message?.spreadId || 'three-card'
        }
      }
      localStorage.setItem('readingMetadata', JSON.stringify(readingMetadata))

      // Prepare metadata for the reading
      const metadata = {
        cards: cards,
        selectedReading: {
          type: step1Message?.spreadId || 'three-card'
        }
      }

      // Start generating the reading
      setIsGeneratingReading(true)

      try {
        // Call the reading API
        const result = await getTextTarotReading(metadata)

        if (result && result.interpretation?.detailed_reading) {
          const detailedReading = result.interpretation.detailed_reading

          // Prepare direct answer - use direct_answer or first sentence of final_conclusion
          const directAnswer = detailedReading.direct_answer ||
            (detailedReading.final_conclusion ? detailedReading.final_conclusion.split('.')[0] + '.' : '')

          // Add the reading result as a message with structured data for DirectAnswerCard
          const readingMessage: Message = {
            step: 2,
            id: `reading-${Date.now()}`,
            spreadData: {
              spreadType: step1Message?.spreadId || 'three-card',
              cards: cards, // Use the actual selected cards
              directAnswer: detailedReading.direct_answer,
              finalConclusion: detailedReading.final_conclusion,
              practicalAdvice: result.interpretation?.practical_advice || [],
              cardInterpretations: detailedReading.cards || [],
              cardInteractions: detailedReading.card_interactions || [],
              readingId: result._id || result.id // Store the reading ID for evaluation
            },
            content: directAnswer,
            role: "assistant" as const,
            timestamp: new Date(),
          }
          addMessage(readingMessage)
        }
      } catch (error) {
        console.error('Error generating reading:', error)
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          content: t('welcomeSpread.readingError') || 'Sorry, there was an error generating your reading. Please try again.',
          role: "assistant" as const,
          timestamp: new Date(),
        }
        addMessage(errorMessage)
      } finally {
        setIsGeneratingReading(false)
      }
    }
  }, [closeSelectionDrawer, addMessage, t, step1Message, getTextTarotReading, setDrawnCards])

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 124px)', // Account for header and footer
        bgcolor: '#F8F8F8',
        position: 'relative'
      }}>
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          pb: '100px', // Add padding for fixed input at bottom
          bgcolor: 'background.default'
        }}>
          <MessageList messages={enrichedMessages} isTyping={isTyping} />

          {error && (
            <Box sx={{
              p: 2,
              bgcolor: 'error.light',
              color: 'error.contrastText',
              borderRadius: 1,
              mt: 2
            }}>
              {error}
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        <Box
          component="form"
          onSubmit={handleSendMessage}
          sx={{
            position: 'fixed',
            bottom: '60px', // Account for footer height
            left: 0,
            right: 0,
            p: 2,
            borderTop: '1px solid rgba(201, 173, 167, 0.4)',
            bgcolor: 'background.paper',
            zIndex: 10
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              placeholder={t('welcomeSpread.placeholder')}
              value={input}
              onChange={handleInputChange}
              disabled={isLoading}
              multiline
              maxRows={4}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  borderRadius: '12px',
                  border: '1px solid rgba(201, 173, 167, 0.4)',
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                  '&:hover fieldset': {
                    borderColor: 'transparent',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'transparent',
                  },
                },
                '& .MuiInputBase-input': {
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: '1rem',
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!input.trim() || isLoading}
              sx={{
                minWidth: '50px',
                height: '50px',
                borderRadius: '12px',
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light'
                },
                '&:active': {
                  bgcolor: 'primary.dark'
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled'
                }
              }}
            >
              <Send />
            </Button>
          </Box>
        </Box>

        {/* Card Selection Drawer */}
        <SwipeableDrawer
          anchor="bottom"
          open={selectionDrawerOpen}
          onClose={closeSelectionDrawer}
          onOpen={() => setSelectionDrawerOpen(true)}
          slotProps={{
            paper: {
              sx: {
                height: '100%',
                padding: '20px',
              }
            }
          }}
        >
          <CardSelection
            maxSelections={step1Message?.spreadId ? spreadCardCounts[step1Message.spreadId] || 3 : 3}
            key={selectionKey}
            onSelectionComplete={handleSelectionComplete}
          />
        </SwipeableDrawer>
      </Box>
    </ThemeProvider>
  )
}

export default WelcomeSpread;
